import type { MetaDescriptor } from 'react-router';
import type { Breadcrumb } from '~/components/PageTemplate/Breadcrumbs';

export interface FaqItem {
  question: string;
  answer: string;
}

export interface SoftwareApplicationOffer {
  name: string;
  description: string;
  /** Lowest price, eg 0 for a free tier. */
  lowPrice: number;
  /** Starting price of the cheapest paid plan. */
  highPrice: number;
  priceCurrency: string;
  url: string;
}

export interface PageMetadata {
  title?: string;
  description?: string;
  image?: string;
  canonical?: string;
  breadcrumbs?: Breadcrumb[];
  faq?: FaqItem[];
  softwareApplication?: SoftwareApplicationOffer;
  author?: string;
  /** ISO 8601 date, eg 2026-10-13. Marks the page as og:type=article. */
  publishedTime?: string;
}
export function getPageMetaTags({
  title,
  description,
  image,
  canonical,
  breadcrumbs,
  faq,
  softwareApplication,
  author,
  publishedTime,
}: PageMetadata): MetaDescriptor[] {
  const metaTags: MetaDescriptor[] = [
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      property: 'og:type',
      content: publishedTime ? 'article' : 'website',
    },
  ];
  if (author) {
    metaTags.push(
      {
        name: 'author',
        content: author,
      },
      {
        property: 'article:author',
        content: author,
      }
    );
  }
  if (publishedTime) {
    metaTags.push({
      property: 'article:published_time',
      content: publishedTime,
    });
  }
  if (title) {
    metaTags.push({
      title,
    });
    metaTags.push({
      property: 'og:title',
      content: title,
      name: 'title',
    });
  }
  if (description) {
    metaTags.push(
      {
        name: 'description',
        content: description,
      },
      {
        property: 'og:description',
        content: description,
        name: 'description',
      }
    );
  }
  if (image) {
    metaTags.push(
      {
        name: 'image',
        property: 'og:image',
        content: image,
      },
      {
        name: 'twitter:image',
        content: image,
      }
    );
  }
  if (canonical) {
    metaTags.push({
      tagName: 'link',
      rel: 'canonical',
      href: canonical,
    });
    metaTags.push({
      property: 'og:url',
      content: canonical,
      name: 'canonical',
    });
  }
  if (breadcrumbs) {
    metaTags.push({
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((breadcrumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: breadcrumb.text,
          item: breadcrumb.href,
        })),
      },
    });
  }

  if (faq && faq.length > 0) {
    metaTags.push({
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map(({ question, answer }) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: answer,
          },
        })),
      },
    });
  }

  if (softwareApplication) {
    metaTags.push({
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: softwareApplication.name,
        description: softwareApplication.description,
        url: softwareApplication.url,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Any (SaaS)',
        offers: {
          '@type': 'AggregateOffer',
          url: softwareApplication.url,
          priceCurrency: softwareApplication.priceCurrency,
          lowPrice: softwareApplication.lowPrice,
          highPrice: softwareApplication.highPrice,
        },
      },
    });
  }

  return metaTags;
}
